import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LockKeyhole, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, getToken, setToken } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken()) {
      navigate("/admin/inbox", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Please enter both username and password.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { username: username.trim(), password });
      setToken(res.data.token);
      navigate("/admin/inbox", { replace: true });
    } catch (err) {
      setError(
        err?.response?.status === 401
          ? "Invalid username or password."
          : "Unable to log in right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background hero-backdrop noise-overlay flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 mb-6"
          data-testid="admin-back-to-site-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to website
        </Link>

        <div className="card-highlight rounded-2xl bg-card border border-border p-8 shadow-[0_25px_80px_rgba(0,0,0,0.55)]">
          <div className="flex items-center gap-3 mb-2">
            <img src="/fcy-mark.png" alt="FCY IT Consultancy logo" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-foreground">Owner Access</h1>
              <p className="text-xs text-muted-foreground">Private message inbox</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-7 space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-username" className="text-xs uppercase tracking-widest text-muted-foreground">
                Username
              </Label>
              <Input
                id="admin-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                autoComplete="username"
                className="bg-secondary/50 border-border h-11 rounded-xl focus-visible:ring-primary"
                data-testid="admin-login-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-xs uppercase tracking-widest text-muted-foreground">
                Password
              </Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                className="bg-secondary/50 border-border h-11 rounded-xl focus-visible:ring-primary"
                data-testid="admin-login-password-input"
              />
            </div>

            {error && (
              <p
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-xl px-4 py-3"
                aria-live="polite"
                data-testid="admin-login-error"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl h-11 disabled:opacity-60"
              data-testid="admin-login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...
                </>
              ) : (
                <>
                  <LockKeyhole className="mr-2 h-4 w-4" /> Sign In
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="mt-5 text-center text-xs text-muted-foreground/60">
          Restricted area — authorised access only.
        </p>
      </motion.div>
    </div>
  );
}
